import { ModalV3 } from "src/components/ModalV3/ModalV3.tsx";
import { ExonnHub } from "src/admin/modules/b2b-gate-order/components/ExonnHub/ExonnHub.tsx";
import { useCallback, useEffect, useRef } from "react";

import { DEMO_SHOP_LOGIN, DEMO_SHOP_PASSWORD } from "src/common/constants.ts";
import { LoadingOverlay } from "src/components/LoadingOverlay/LoadingOverlay.tsx";
import { useExonnApi } from "src/exonn-api/exonn-api.tsx";
import { useList } from "src/system/useList.tsx";
import { ResourceEnum } from "src/constants/constants.ts";
import type { B2bGateOrderResource } from "@exonn/js-sdk";
import { last } from "@exonn/utils";
import { css } from "@emotion/react";

import { useCreateOne } from "src/system/useCreateOne.ts";
import { useQueryClient } from "@tanstack/react-query";
import { useLogin, useUser } from "src/auth/auth.ts";
import { TranslationNamespaceEnum } from "src/lib/i18next/types.ts";
import { useTranslation } from "react-i18next";

export const DemoShop = () => {
  const { data: user, isLoading: isUserLoading } = useUser();
  const { mutate: login } = useLogin();
  const api = useExonnApi();
  const initializingRef = useRef(false);
  const { t } = useTranslation();

  const init = useCallback(async () => {
    if (!isUserLoading && !user && !initializingRef.current) {
      initializingRef.current = true;

      await api.auth.initializeCsrfCookie();
      login({
        login: DEMO_SHOP_LOGIN,
        password: DEMO_SHOP_PASSWORD,
      });

      initializingRef.current = false;
    }
  }, [login, user, isUserLoading, api]);

  useEffect(() => {
    init();
  }, [init]);

  if (!user) {
    return (
      <LoadingOverlay.Root isLoading={true}>
        <LoadingOverlay.Spinner />
        <LoadingOverlay.Title>
          {t("advices.pleaseWait", {
            ns: TranslationNamespaceEnum.Common,
          })}
        </LoadingOverlay.Title>
      </LoadingOverlay.Root>
    );
  }

  return <DemoShopImpl />;
};

const DemoShopImpl = () => {
  const {
    data: orderListData,
    isFetching: isOrderListFetching,
    isLoading,
  } = useList<B2bGateOrderResource>({
    resource: ResourceEnum.B2bGateOrder,
    disablePagination: true,
  });

  const createdOrderRef = useRef(false);
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const { mutate: createOrder, isPending: isCreatingOrder } = useCreateOne<any>(
    {
      resource: ResourceEnum.B2bGateOrder,
    },
  );

  const order = last(orderListData?.data ? orderListData.data : []);

  useEffect(() => {
    const isCreating = queryClient.isMutating({
      mutationKey: [ResourceEnum.B2bGateOrder],
    });
    if (
      !isLoading &&
      orderListData!.data.length < 1 &&
      !isCreating &&
      !createdOrderRef.current
    ) {
      createOrder(
        {
          data: {},
          locale: "de",
        },
        {
          onSuccess: () => {
            createdOrderRef.current = true;
          },
        },
      );
    }
  }, [orderListData, createOrder, isLoading, queryClient]);

  if (isOrderListFetching || isCreatingOrder) {
    return (
      <LoadingOverlay.Root isLoading={true}>
        <LoadingOverlay.Spinner />
        <LoadingOverlay.Title>
          {t("advices.pleaseWait", {
            ns: TranslationNamespaceEnum.Common,
          })}
        </LoadingOverlay.Title>
      </LoadingOverlay.Root>
    );
  }

  if (!order) {
    return null;
  }

  return (
    <div css={rootStyles}>
      <ModalV3>
        <ModalV3.Positioner positionX={"center"} positionY={"center"}>
          <ModalV3.Content>
            <ExonnHub initialIsFullscreen={true} orderId={order.id} />
          </ModalV3.Content>
        </ModalV3.Positioner>
      </ModalV3>
    </div>
  );
};

const rootStyles = css`
  height: 100dvh;
  width: 100dvw;
`;
